import { useMemo } from 'react';
import { useListCatalogQuery, useListCustomersQuery, useListVendorsQuery } from './apiSlice.js';

const ACTIVE = { activeOnly: true };

/**
 * The dropdown data for Stock In, Stock Out, Pricing and Reports.
 *
 * This used to be a context provider that fetched once and handed the result
 * down. RTK Query already dedupes identical requests across components and
 * refetches them when a tag is invalidated, so the provider is gone: adding a
 * texture on the Texture page updates the Stock In dropdown by itself.
 */
export function useReferenceData() {
  const products = useListCatalogQuery({ kind: 'productName', ...ACTIVE });
  const textures = useListCatalogQuery({ kind: 'texture', ...ACTIVE });
  const lengths = useListCatalogQuery({ kind: 'length', ...ACTIVE });
  const colors = useListCatalogQuery({ kind: 'color', ...ACTIVE });
  const vendors = useListVendorsQuery(ACTIVE);
  const customers = useListCustomersQuery(ACTIVE);

  const parts = [products, textures, lengths, colors, vendors, customers];

  return useMemo(
    () => ({
      products: (products.data || []).map((row) => row.name),
      textures: (textures.data || []).map((row) => row.name),
      lengths: (lengths.data || []).map((row) => row.name),
      colors: (colors.data || []).map((row) => row.name),
      vendors: (vendors.data || []).map((row) => row.company),
      customers: (customers.data || []).map((row) => ({ company: row.company, customerType: row.customerType })),
      loading: parts.some((part) => part.isLoading),
      error: parts.find((part) => part.error)?.error?.message || null,
      reload: () => Promise.all(parts.map((part) => part.refetch())),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    parts
  );
}

export default useReferenceData;
